/**
 * PostHog Analytics Event Definitions
 * 
 * Type-safe event names and property types for consistent tracking across the app.
 */

// ============================================
// EVENT NAMES
// ============================================

export const ANALYTICS_EVENTS = {
  // Authentication Events
  USER_SIGNED_UP: "user_signed_up",
  USER_LOGGED_IN: "user_logged_in",
  USER_LOGGED_OUT: "user_logged_out",

  // Practice Events
  PRACTICE_SESSION_STARTED: "practice_session_started",
  PRACTICE_SESSION_COMPLETED: "practice_session_completed",
  PRACTICE_SESSION_ABANDONED: "practice_session_abandoned",
  QUESTION_ANSWERED: "question_answered",
  QUESTION_FLAGGED: "question_flagged",
  QUESTION_UNFLAGGED: "question_unflagged",

  // Notes Events
  NOTES_VIEWED: "notes_viewed",
  NOTES_SECTION_VIEWED: "notes_section_viewed",

  // Study/Navigation Events
  SUBJECT_SELECTED: "subject_selected",
  PRACTICE_SETUP_STARTED: "practice_setup_started",
  PRACTICE_FILTERS_APPLIED: "practice_filters_applied",
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

// ============================================
// EVENT PROPERTY TYPES
// ============================================

export interface UserSignedUpProperties {
  method: "google" | "email";
  referrer?: string;
}

export interface UserLoggedInProperties {
  method: "google" | "email";
}

export interface PracticeSessionStartedProperties {
  subjectSlug: string;
  subjectName?: string;
  unitIds: string[];
  topicSlugs: string[];
  difficulty: string;
  questionCount: number;
}

export interface PracticeSessionCompletedProperties {
  subjectSlug: string;
  subjectName?: string;
  totalQuestions: number;
  correctCount: number;
  incorrectCount: number;
  unansweredCount: number;
  accuracy: number; // 0-100 percentage
  durationSeconds: number;
}

export interface PracticeSessionAbandonedProperties {
  subjectSlug: string;
  questionsAnswered: number;
  totalQuestions: number;
}

export interface QuestionAnsweredProperties {
  subjectSlug: string;
  unitId: string;
  topicSlug?: string;
  difficulty: string;
  isCorrect: boolean;
  timeSeconds?: number;
  questionId: string;
}

export interface QuestionFlaggedProperties {
  subjectSlug: string;
  questionId: string;
}

export interface NotesViewedProperties {
  subjectSlug: string;
  unitId: string;
  unitName?: string;
}

export interface NotesSectionViewedProperties {
  subjectSlug: string;
  unitId: string;
  sectionId: string;
  sectionTitle?: string;
}

export interface SubjectSelectedProperties {
  subjectSlug: string;
  subjectName?: string;
}

export interface PracticeSetupStartedProperties {
  subjectSlug: string;
}

export interface PracticeFiltersAppliedProperties {
  subjectSlug: string;
  unitIds: string[];
  topicSlugs: string[];
  difficulty: string;
  questionCount: number;
}

// ============================================
// USER PROPERTIES (for identify calls)
// ============================================

export interface UserProperties {
  email?: string;
  name?: string;
  createdAt?: string;
  totalQuestionsAnswered?: number;
  totalCorrectAnswers?: number;
  lastActiveAt?: string;
  subjectsStudied?: string[];
}
